import { Button } from 'components/common'
import ClearFiltersButton from 'components/common/clearFiltersButton'
import {
  CronJobFilters,
  CronJobSummary,
  CronJobsTable,
  EditCronJobModal,
  ResetCronJobModal,
} from 'components/cronJobs'
import { usePageAccess } from 'hooks'
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useLocation } from 'react-router-dom'
import useLogFilters from 'hooks/useLogFilters'
import { FILTER_FIELD_CRON_JOBS } from 'components/cronJobs/utils'
import { CronJob, CronJobHealth, CronJobsResponse } from 'types/cronJob'
import { fetchCronJobs } from 'utils/api/cronJobsApi'
import toast from 'react-hot-toast'

const EMPTY_RESULT: CronJobsResponse = { jobs: [], groups: [], summary: {} }

// Background refresh while the tab is visible
const AUTO_REFRESH_MS = 30 * 1000

const CronJobs = () => {
  const { filters, setField } = useLogFilters(FILTER_FIELD_CRON_JOBS)
  const { search } = useLocation()

  const [result, setResult] = useState<CronJobsResponse>(EMPTY_RESULT)
  // Only the very first load shows the grid's loading overlay
  const [hasLoaded, setHasLoaded] = useState(false)
  // Spins the Refresh button; background refreshes leave it alone
  const [loading, setLoading] = useState(false)

  // The job waiting for reset confirmation in the popup
  const [jobToReset, setJobToReset] = useState<CronJob>()
  // The job open in the edit popup
  const [jobToEdit, setJobToEdit] = useState<CronJob>()

  const { isEditor } = usePageAccess()

  // silent: background refresh, no spinner and no error toast
  const loadCronJobs = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)

    try {
      const { data } = await fetchCronJobs(filters)

      // Keep the same object when nothing changed, so the grid isn't redrawn
      setResult((previous) =>
        JSON.stringify(previous) === JSON.stringify(data) ? previous : data,
      )
      setHasLoaded(true)
    } catch {
      if (!silent) toast.error('Failed to load cron jobs.')
    } finally {
      if (!silent) setLoading(false)
    }
  }

  // Load when the filters in the URL change, then refresh in the background
  // every 30s while the tab is visible (and right away when it comes back)
  useEffect(() => {
    loadCronJobs()

    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') loadCronJobs({ silent: true })
    }

    const timer = setInterval(refreshIfVisible, AUTO_REFRESH_MS)
    document.addEventListener('visibilitychange', refreshIfVisible)

    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', refreshIfVisible)
    }
  }, [search])

  // Clicking a summary card filters by that health ('' clears it)
  const selectHealth = (health: CronJobHealth | '') =>
    setField('health', health || 'NONE')

  return (
    <>
      <Helmet>
        <title>GAG Aggregator | Cron Jobs</title>
      </Helmet>

      <div className="mb-3 flex items-center justify-between">
        <div>
          <h1 className="title text-2xl">Cron Jobs</h1>
          <p className="text-sm text-gray-500">
            Every scheduled job and its latest run.
          </p>
        </div>
        <div className="flex items-center gap-1">
          <ClearFiltersButton filterKey={FILTER_FIELD_CRON_JOBS} />
          <Button
            type="button"
            variant="outline"
            buttonText="Refresh"
            loading={loading}
            onClick={() => loadCronJobs()}
          />
        </div>
      </div>

      <CronJobSummary
        summary={result.summary}
        selectedHealth={filters.health}
        onSelect={selectHealth}
      />

      <CronJobFilters groups={result.groups} />

      <CronJobsTable
        jobs={result.jobs}
        loading={!hasLoaded}
        isEditor={isEditor}
        onEdit={setJobToEdit}
        onReset={setJobToReset}
      />

      <EditCronJobModal
        key={jobToEdit?._id}
        job={jobToEdit}
        isEditor={isEditor}
        closeModal={() => setJobToEdit(undefined)}
        onDone={loadCronJobs}
      />

      <ResetCronJobModal
        job={jobToReset}
        closeModal={() => setJobToReset(undefined)}
        onDone={loadCronJobs}
      />
    </>
  )
}

export default CronJobs
